import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CircularCardCarousel.css";

export default function CircularCardCarousel({
  title,
  subtitle = '',
  items = [],
  itemsPerSlide = 3,
  slideMode = "chunk",
  autoplayMs = 4000
}) {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);

  const slides = useMemo(() => {
    if (!items.length) return [];

    if (slideMode === "window") {
      return items.map((_, startIndex) =>
        Array.from({ length: itemsPerSlide }, (_, offset) => {
          const index = (startIndex + offset) % items.length;
          return items[index];
        })
      );
    }

    const grouped = [];
    for (let i = 0; i < items.length; i += itemsPerSlide) {
      grouped.push(items.slice(i, i + itemsPerSlide));
    }
    return grouped;
  }, [items, itemsPerSlide, slideMode]);

  useEffect(() => {
    if (slides.length <= 1) return undefined;

    const intervalId = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, autoplayMs);

    return () => clearInterval(intervalId);
  }, [autoplayMs, slides.length]);

  useEffect(() => {
    if (activeSlide >= slides.length) {
      setActiveSlide(0);
    }
  }, [activeSlide, slides.length]);

  const goNext = () => {
    if (!slides.length) return;
    setActiveSlide((prev) => (prev + 1) % slides.length);
  };

  const goPrev = () => {
    if (!slides.length) return;
    setActiveSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const handlePointerStart = (clientX) => {
    setIsDragging(true);
    dragStartX.current = clientX;
  };

  const handlePointerEnd = (clientX) => {
    if (!isDragging) return;

    const dragDistance = clientX - dragStartX.current;
    if (dragDistance < -50) goNext();
    if (dragDistance > 50) goPrev();

    setIsDragging(false);
  };

  const handleItemClick = (item) => {
    // prevent navigation when user is dragging/swiping
    if (isDragging) return;
    if (!item?.link) return;
    if (item.state) {
      navigate(item.link, { state: item.state });
      return;
    }
    navigate(item.link);
  };

  return (
    <section className="card-carousel-section" aria-label={title}>
      <h2 className="card-carousel-title">{title}</h2>
      {subtitle && <p className="card-carousel-subtitle">{subtitle}</p>}

      <div
        className={`card-carousel ${isDragging ? "is-dragging" : ""}`}
        onMouseDown={(event) => handlePointerStart(event.clientX)}
        onMouseUp={(event) => handlePointerEnd(event.clientX)}
        onMouseLeave={(event) => handlePointerEnd(event.clientX)}
        onTouchStart={(event) => handlePointerStart(event.touches[0].clientX)}
        onTouchEnd={(event) => handlePointerEnd(event.changedTouches[0].clientX)}
      >
        <div
          className="card-carousel-track"
          style={{ transform: `translateX(-${activeSlide * 100}%)` }}
        >
          {slides.map((slide, slideIndex) => (
  <div key={`slide-${slideIndex}`} className="card-carousel-slide">
    {slide.map((item, itemIndex) => {
      const isCenter = itemIndex === Math.floor(slide.length / 2);

      return (
        <article
          key={`${item.id}-${slideIndex}-${itemIndex}`}
          className={`card-carousel-item ${isCenter ? "center-item" : ""}`}
          onClick={() => handleItemClick(item)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleItemClick(item);
            }
          }}
        >
          <div className="card-carousel-circle">
            <img src={item.image} alt={item.label} draggable="false" />
          </div>
          <p className="card-carousel-label">{item.label}</p>
        </article>
      );
    })}
  </div>
))}
        </div>
      </div>

      {slides.length > 1 && (
        <div className="card-carousel-dots" role="tablist" aria-label={`${title} slides`}>
          {slides.map((_, i) => (
            <button
              key={`dot-${i}`}
              type="button"
              className={`card-carousel-dot ${i === activeSlide ? "active" : ""}`}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setActiveSlide(i)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
